import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaPlus, FaTrash } from 'react-icons/fa'
import useOutsideClick from '../hooks/useOutsideClick'

function GroceriesItems({ allGrocery, GroceryRows, setGroceryRows }) {
  const { t } = useTranslation()

  const [isViewGroceriesList, setIsViewGroceriesList] = useState(false)
  const [isPopover, setIsPopover] = useState(null)
  const [searchInput, setSearchInput] = useState('')

  const searchInputRef = useRef(null);

  React.useEffect(() => {
    if (isPopover !== null && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 50);
    }
  }, [isPopover]);

  const unitTypeList = [
    { key: 'KGS', name: 'કિગ્રા (KGS)' },
    { key: 'Gram', name: 'ગ્રામ (Gram)' },
    { key: 'Liter', name: 'લિટર (Liter)' },
    { key: 'ML', name: 'મિલીલીટર (ML)' },
    { key: 'Pieces', name: 'નંગ (Pieces)' },
  ]

  const popoverRef = useRef(null)

  useOutsideClick(popoverRef, () => {
    setIsPopover(null)
  })

  const showGroceriesList = () => {
    setIsViewGroceriesList(!isViewGroceriesList)
  }

  const togglePopover = (index) => {
    setIsPopover(isPopover === index ? null : index)
    setSearchInput('')
  }

  const handleSearchChange = (e) => {
    setSearchInput(e.target.value)
  }

  const handleGroceryClick = (index, grocery) => {
    setGroceryRows((prevRows) =>
      prevRows.map((row, i) =>
        i === index ? { ...row, grocery, unitType: grocery.unitType } : row
      )
    )
    setIsPopover(null)
    setSearchInput('')
  }

  const addNewRow = () => {
    setGroceryRows([...GroceryRows, { grocery: null, quantity: '' }])
    setSearchInput('')
  }

  const removeRow = (index) => {
    setGroceryRows((prevRows) => prevRows.filter((_, i) => i !== index))
  }

  const clearRow = (index) => {
    setGroceryRows((prevRows) =>
      prevRows.map((row, i) =>
        i === index ? { grocery: null, unitType: '', quantity: '' } : row
      )
    )
  }

  const isLastRowFilled = () => {
    const lastRow = GroceryRows[GroceryRows.length - 1]
    return lastRow.grocery && lastRow.unitType.trim() && lastRow.quantity.trim()
  }

  const handleChange = (index, e) => {
    const { name, value } = e.target
    setGroceryRows((prevRows) =>
      prevRows.map((row, i) => (i === index ? { ...row, [name]: value } : row))
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100">
      {/* Header */}
      <div
        className={`bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4 cursor-pointer ${isViewGroceriesList ? 'rounded-t-2xl' : 'rounded-2xl'}`}
        onClick={(e) => {
          e.stopPropagation()
          showGroceriesList()
        }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">{t('Groceries')}</h3>
              <p className="text-orange-100 text-sm">{t('add_groceries_ingredients')}</p>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation()
              showGroceriesList()
            }}
            className="p-2 bg-white bg-opacity-20 rounded-lg hover:bg-opacity-30 transition-all duration-200"
          >
            <svg
              className={`w-5 h-5 text-white transform transition-transform duration-200 ${isViewGroceriesList ? 'rotate-180' : ''
                }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      {isViewGroceriesList && (
        <div className="p-6">
          <div className="space-y-4 bg-gray-50 rounded-xl p-4 border border-gray-200">
            {GroceryRows.map((row, index) => (
              <div key={index}>
                <div className="grid grid-cols-4 gap-4">
                  {/* Grocery Selection */}
                  <div className="relative" ref={popoverRef}>
                    {index === 0 && (
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('Groceries')}
                      </label>
                    )}
                    <div
                      className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                      onClick={(e) => {
                        e.stopPropagation()
                        togglePopover(index)
                      }}
                    >
                      <span className={row.grocery ? 'text-gray-900' : 'text-gray-500'}>
                        {row.grocery ? row.grocery.name : t('pls_sel_groceries')}
                      </span>
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>

                    {isPopover === index && (
                      <div
                        className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-auto z-[9999] w-full"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-3 border-b border-gray-200">
                          <input
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search groceries..."
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none text-sm"
                            value={searchInput}
                            onChange={handleSearchChange}
                          />
                        </div>
                        <div className="max-h-36 overflow-auto">
                          {allGrocery
                            ?.filter((grocery) =>
                              grocery.name
                                .toLowerCase()
                                .includes(searchInput.toLowerCase().trim())
                            )
                            .map((grocery) => (
                              <div
                                key={grocery._id}
                                className="px-3 py-2 hover:bg-orange-50 cursor-pointer text-sm border-b border-gray-100 last:border-b-0"
                                onClick={() => handleGroceryClick(index, grocery)}
                              >
                                {grocery.name}
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Unit Type */}
                  <div className="relative">
                    {index === 0 && (
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('unit_type')}
                      </label>
                    )}
                    <div className="relative">
                      <select
                        name="unitType"
                        value={row.unitType}
                        onChange={(e) => handleChange(index, e)}
                        className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50 appearance-none cursor-pointer"
                      >
                        <option value="" disabled>
                          {t('select_options')}
                        </option>
                        {unitTypeList.map((unit) => (
                          <option key={unit.key} value={unit.key}>
                            {unit.name}
                          </option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Quantity */}
                  <div className="relative">
                    {index === 0 && (
                      <label className="text-sm font-medium text-gray-700 mb-2 block">
                        {t('Quantity')}
                      </label>
                    )}
                    <input
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*"
                      placeholder={t('Quantity')}
                      className="w-full px-4 py-3 text-gray-900 bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 hover:bg-gray-50"
                      value={row.quantity}
                      onChange={(e) => {
                        const raw = e.target.value
                        const cleaned = raw.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1')
                        setGroceryRows((prevRows) =>
                          prevRows.map((r, i) =>
                            i === index ? { ...r, quantity: cleaned } : r
                          )
                        )
                      }}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-end justify-start">
                    {GroceryRows.length - 1 === index ? (
                      <div className="flex gap-2">
                        <button
                          className="px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={addNewRow}
                          disabled={!isLastRowFilled()}
                        >
                          <FaPlus className="w-4 h-4 mr-2" />
                          {t('btnAddNew')}
                        </button>
                        <button
                          className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all duration-200 flex items-center justify-center"
                          onClick={() => clearRow(index)}
                        >
                          {t('btnReset')}
                        </button>
                      </div>
                    ) : (
                      <button
                        className="px-4 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 flex items-center justify-center"
                        onClick={() => removeRow(index)}
                      >
                        <FaTrash className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default GroceriesItems
